import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompetition } from './CompetitionContext';
import { useAuth } from '../auth/AuthContext';
import SideMenu from '../components/SideMenu';
import './CompetitionSelection.css';

const CompetitionSelection = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { competitions, fetchCompetitions, createCompetition, updateCompetition, uploadImage, selectCompetition, loading } = useCompetition();
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  // Form state for new competition
  const [formData, setFormData] = useState({
    name: '',
    venue: '',
    location: '',
    startdate: '',
    enddate: '',
    thumbnail: ''
  });

  // File state for image upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    loadCompetitions();
  }, []);

  const loadCompetitions = async () => {
    setIsLoading(true);
    const result = await fetchCompetitions();
    if (!result.success) {
      setError(result.error);
    }
    setIsLoading(false);
  };

  const handleCompetitionSelect = (competition) => {
    selectCompetition(competition);
    navigate('/administration');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPEG, PNG, etc.)');
        return;
      }

      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsUploading(true);

    try {
      let thumbnailUrl = formData.thumbnail;

      // Upload image if file is selected
      if (selectedFile) {
        const uploadResult = await uploadImage(selectedFile);
        if (uploadResult.success) {
          thumbnailUrl = uploadResult.publicUrl;
        } else {
          setError(uploadResult.error);
          setIsUploading(false);
          return;
        }
      }

      const competitionData = {
        ...formData,
        thumbnail: thumbnailUrl,
        startdate: new Date(formData.startdate).getTime(),
        enddate: new Date(formData.enddate).getTime()
      };

      const result = await createCompetition(competitionData);
      if (result.success) {
        setShowAddForm(false);
        setFormData({
          name: '',
          venue: '',
          location: '',
          startdate: '',
          enddate: '',
          thumbnail: ''
        });
        setSelectedFile(null);
        setImagePreview('');
        // Automatically select the newly created competition
        handleCompetitionSelect(result.competition);
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error creating competition:', error);
      setError('An unexpected error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setFormData({
      name: '',
      venue: '',
      location: '',
      startdate: '',
      enddate: '',
      thumbnail: ''
    });
    setSelectedFile(null);
    setImagePreview('');
    setError('');
  };

  const handleEditCompetition = (competition) => {
    setEditingCompetition(competition);
    setFormData({
      name: competition.name,
      venue: competition.venue,
      location: competition.location,
      startdate: new Date(competition.startdate).toISOString().slice(0, 16),
      enddate: new Date(competition.enddate).toISOString().slice(0, 16),
      thumbnail: competition.thumbnail || ''
    });
    setImagePreview(competition.thumbnail || '');
    setError('');
    setShowEditForm(true);
  };

  const handleUpdateCompetition = async (e) => {
    e.preventDefault();
    setError('');
    setIsUploading(true);

    try {
      let thumbnailUrl = formData.thumbnail;

      // Upload image if file is selected
      if (selectedFile) {
        const uploadResult = await uploadImage(selectedFile);
        if (uploadResult.success) {
          thumbnailUrl = uploadResult.publicUrl;
        } else {
          setError(uploadResult.error);
          setIsUploading(false);
          return;
        }
      }

      const competitionData = {
        ...formData,
        thumbnail: thumbnailUrl,
        startdate: new Date(formData.startdate).getTime(),
        enddate: new Date(formData.enddate).getTime()
      };

      const result = await updateCompetition(editingCompetition.id, competitionData);
      if (result.success) {
        setShowEditForm(false);
        setEditingCompetition(null);
        setSelectedFile(null);
        setImagePreview('');
        setFormData({
          name: '',
          venue: '',
          location: '',
          startdate: '',
          enddate: '',
          thumbnail: ''
        });
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error updating competition:', error);
      setError('An unexpected error occurred');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingCompetition(null);
    setFormData({
      name: '',
      venue: '',
      location: '',
      startdate: '',
      enddate: '',
      thumbnail: ''
    });
    setSelectedFile(null);
    setImagePreview('');
    setError('');
  };

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading competitions...</p>
      </div>
    );
  }

  return (
    <div className="app-with-sidemenu">
      <SideMenu />
      <div className="main-content">
        <div className="competition-selection-container">
          <div className="competition-header">
            <h1>Competition Selection</h1>
          </div>

      <div className="competition-content">
        <div className="competition-actions">
          <h2>Select a Competition</h2>
          <button
            onClick={() => setShowAddForm(true)}
            className="add-competition-btn"
          >
            + Add New Competition
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="competitions-grid">
          {competitions.length === 0 ? (
            <div className="no-competitions">
              <p>No competitions found. Create your first competition to get started.</p>
            </div>
          ) : (
            competitions.map((competition) => (
              <div
                key={competition.id}
                className="competition-card"
              >
                <div className="competition-actions-overlay">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditCompetition(competition);
                    }}
                    className="edit-competition-btn"
                    title="Edit Competition"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCompetitionSelect(competition);
                    }}
                    className="select-competition-btn"
                    title="Select Competition"
                  >
                    ✓
                  </button>
                </div>
                <div onClick={() => handleCompetitionSelect(competition)}>
                  {competition.thumbnail && (
                    <div className="competition-thumbnail">
                      <img src={competition.thumbnail} alt={competition.name} />
                    </div>
                  )}
                  <div className="competition-info">
                    <h3>{competition.name}</h3>
                    <p className="competition-venue">{competition.venue}</p>
                    <div className="competition-dates">
                      <span>{formatTimestamp(competition.startdate)}</span>
                      <span>-</span>
                      <span>{formatTimestamp(competition.enddate)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New Competition</h2>
              <button
                onClick={() => setShowAddForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="competition-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Competition Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., National Championships 2025"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="venue">Venue *</label>
                  <input
                    type="text"
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., The Station, Durban"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="location">Location Coordinates *</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., -29.846598, 31.023428"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startdate">Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    id="startdate"
                    name="startdate"
                    value={formData.startdate}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="enddate">End Date & Time *</label>
                  <input
                    type="datetime-local"
                    id="enddate"
                    name="enddate"
                    value={formData.enddate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="thumbnail">Competition Thumbnail</label>
                <div className="file-upload-container">
                  <input
                    type="file"
                    id="thumbnail"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div className="file-upload-label">
                    {selectedFile ? selectedFile.name : 'Click to select image or drag and drop'}
                  </div>
                  {(imagePreview || formData.thumbnail) && (
                    <div className="image-preview-container">
                      <img
                        src={imagePreview || formData.thumbnail}
                        alt="Thumbnail preview"
                        className="image-preview"
                      />
                    </div>
                  )}
                </div>
                <p className="file-upload-help">Recommended: 16:9 aspect ratio, max 5MB (JPEG, PNG)</p>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="cancel-btn"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isUploading}>
                  {isUploading ? 'Creating Competition...' : 'Create Competition'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Competition</h2>
              <button
                onClick={handleCancelEdit}
                className="close-btn"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleUpdateCompetition} className="competition-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Competition Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., National Championships 2025"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="venue">Venue *</label>
                  <input
                    type="text"
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., The Station, Durban"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="location">Location Coordinates *</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., -29.846598, 31.023428"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="startdate">Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    id="startdate"
                    name="startdate"
                    value={formData.startdate}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="enddate">End Date & Time *</label>
                  <input
                    type="datetime-local"
                    id="enddate"
                    name="enddate"
                    value={formData.enddate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="thumbnail">Competition Thumbnail</label>
                <div className="file-upload-container">
                  <input
                    type="file"
                    id="thumbnail"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div className="file-upload-label">
                    {selectedFile ? selectedFile.name : 'Click to select new image or drag and drop'}
                  </div>
                  {(imagePreview || formData.thumbnail) && (
                    <div className="image-preview-container">
                      <img
                        src={imagePreview || formData.thumbnail}
                        alt="Thumbnail preview"
                        className="image-preview"
                      />
                    </div>
                  )}
                </div>
                <p className="file-upload-help">Recommended: 16:9 aspect ratio, max 5MB (JPEG, PNG)</p>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="cancel-btn"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isUploading}>
                  {isUploading ? 'Updating Competition...' : 'Update Competition'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default CompetitionSelection;